import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeProvider';
import { useSettings } from '@/hooks/useSettings';
import { ThemeSelection, ThemeTokens } from '@/types/theme';
import { supabase } from '@/integrations/supabase/client';
import { getDefaultTokens } from '@/lib/themeUtils';
import { normalizeThemeRow, normalizeThemeSelection } from '@/lib/themeResolver';
import { isAppRoot, isPanelPage } from "@/lib/utils"
import {
  getAlbumSlugFromPathname,
  getBootstrappedTheme,
  readThemeCache,
  writeThemeCacheForSlug,
} from '@/lib/themeCache';
import { useLocation } from 'react-router-dom';
import { useAlbumContext } from '@/contexts/AlbumProvider';

/**
 * Simplified hook that waits for settings to load and applies the theme
 */
export const useThemeFromSettings = () => {
  const { applyTokens, setThemeLoaded } = useTheme();
  const { getSetting, loading: isFetchingSettings } = useSettings();
  const appliedThemeKeyRef = useRef<string | null>(null);
  const bootstrappedThemeRef = useRef(getBootstrappedTheme());
  const location = useLocation();
  const { albumId, loading: isResolvingAlbum } = useAlbumContext();
  const isRoot = isAppRoot(location);
  const isPanel = isPanelPage(location);
  const routeAlbumSlug = getAlbumSlugFromPathname(location.pathname);

  useLayoutEffect(() => {
    if (isRoot || isPanel || !routeAlbumSlug) {
      return;
    }

    const cachedTheme = readThemeCache(routeAlbumSlug);
    if (!cachedTheme) {
      return;
    }

    const cachedThemeKey = `cached:${routeAlbumSlug}:${cachedTheme.selectionKey}`;
    if (
      appliedThemeKeyRef.current === cachedThemeKey ||
      appliedThemeKeyRef.current === `${albumId}:${cachedTheme.selectionKey}`
    ) {
      setThemeLoaded();
      return;
    }

    const isBootstrappedTheme =
      bootstrappedThemeRef.current?.slug === routeAlbumSlug &&
      bootstrappedThemeRef.current?.selectionKey === cachedTheme.selectionKey;

    if (!isBootstrappedTheme) {
      applyTokens(cachedTheme.tokens, true);
    }

    appliedThemeKeyRef.current = cachedThemeKey;
    setThemeLoaded();
  }, [albumId, applyTokens, isPanel, isRoot, routeAlbumSlug, setThemeLoaded]);

  useEffect(() => {
    if (isFetchingSettings) {
      return;
    }

    let isCancelled = false;

    const applyDefaultTheme = (themeKey: string, selectionKey: string) => {
      if (isCancelled) {
        return;
      }

      const defaultTokens = getDefaultTokens();
      applyTokens(defaultTokens);
      appliedThemeKeyRef.current = themeKey;
      writeThemeCacheForSlug({
        slug: routeAlbumSlug,
        selectionKey,
        tokens: defaultTokens,
      });
    };

    const applyResolvedTokens = (
      themeKey: string,
      selectionKey: string,
      tokens: ThemeTokens,
      options?: { usingCachedTokens?: true; themeId?: string }
    ) => {
      if (isCancelled) {
        return;
      }

      applyTokens(tokens, options?.usingCachedTokens);
      appliedThemeKeyRef.current = themeKey;
      if (!options?.usingCachedTokens) {
        writeThemeCacheForSlug({
          slug: routeAlbumSlug,
          selectionKey,
          themeId: options?.themeId,
          tokens,
        });
      }
    };

    const syncCachedTheme = (selectionKey: string, themeKey: string) => {
      if (!routeAlbumSlug) {
        return false;
      }

      const cachedTheme = readThemeCache(routeAlbumSlug);
      if (!cachedTheme || cachedTheme.selectionKey !== selectionKey) {
        return false;
      }

      const cachedThemeKey = `cached:${routeAlbumSlug}:${selectionKey}`;
      const isBootstrappedTheme =
        bootstrappedThemeRef.current?.slug === routeAlbumSlug &&
        bootstrappedThemeRef.current?.selectionKey === selectionKey;

      if (
        appliedThemeKeyRef.current !== cachedThemeKey &&
        appliedThemeKeyRef.current !== themeKey &&
        !isBootstrappedTheme
      ) {
        applyTokens(cachedTheme.tokens, true);
      }

      appliedThemeKeyRef.current = themeKey;
      setThemeLoaded();
      return true;
    };

    const applyThemeFromSelection = async (
      themeSelectionString: string,
      themeKey: string,
      selectionKey: string
    ): Promise<void> => {
      try {
        if (!themeSelectionString) {
          applyDefaultTheme(themeKey, selectionKey);
          return;
        }

        const selection: ThemeSelection = JSON.parse(themeSelectionString);

        if (selection.type === 'custom') {
          const normalizedTheme = normalizeThemeSelection(selection);
          applyResolvedTokens(themeKey, selectionKey, normalizedTheme.tokens, {
            themeId: selection.theme_id,
          });
          return;
        }

        if (selection.type === 'preset' && selection.theme_id) {
          const { data: theme, error } = await supabase
            .from('themes')
            .select('id, name, colors, fonts, seed')
            .eq('id', selection.theme_id)
            .single();

          if (isCancelled) {
            return;
          }

          if (error) {
            console.error('Error fetching preset theme:', error);
            applyDefaultTheme(themeKey, selectionKey);
            return;
          }

          if (theme) {
            const normalizedTheme = normalizeThemeRow(theme);
            applyResolvedTokens(themeKey, selectionKey, normalizedTheme.tokens, {
              themeId: selection.theme_id,
            });
            return;
          }

          applyDefaultTheme(themeKey, selectionKey);
          return;
        }

        applyDefaultTheme(themeKey, selectionKey);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error('[Theme] Error parsing/applying theme:', error);
        applyDefaultTheme(themeKey, selectionKey);
      }
    };

    const run = async () => {
      if (isRoot || isPanel || !routeAlbumSlug) {
        const themeKey = `default:${isRoot ? 'root' : isPanel ? 'panel' : 'app'}:${location.pathname}`;
        if (appliedThemeKeyRef.current === themeKey) {
          setThemeLoaded();
          return;
        }

        applyTokens(getDefaultTokens(), true);
        appliedThemeKeyRef.current = themeKey;
        return;
      }

      if (isResolvingAlbum || !albumId) {
        return;
      }

      const selection = getSetting('theme_selection', '');
      const selectionKey = selection || 'default';
      const themeKey = `${albumId}:${selectionKey}`;
      if (appliedThemeKeyRef.current === themeKey) {
        setThemeLoaded();
        return;
      }

      if (syncCachedTheme(selectionKey, themeKey)) {
        return;
      }

      await applyThemeFromSelection(selection, themeKey, selectionKey);
    };

    void run();

    return () => {
      isCancelled = true;
    };
  }, [
    albumId,
    applyTokens,
    getSetting,
    isFetchingSettings,
    isPanel,
    isResolvingAlbum,
    isRoot,
    location.pathname,
    routeAlbumSlug,
    setThemeLoaded,
  ]);

  return {
    loading: isFetchingSettings
  };
};
